import { Can } from '@/components/can/Can';
import CrumbTitle from '@/components/crumbTitle/CrumbTitle';
import { Button } from '@/design-system/button';
import { PERMS } from '@/features/auth/permissions/permissionKeys';
import { AssignDiscountForm } from '@/features/configuration-management/discount-management/AssignDiscountForm';
import { DiscountCategoryTable } from '@/features/configuration-management/discount-management/category/table';
import { useGetDiscountCategories } from '@/features/configuration-management/discount-management/hooks/useGetDiscountCategories';
import { PercentageIcon } from '@/icons';
import { createFileRoute, SearchSchemaInput } from '@tanstack/react-router';
import { z } from 'zod';

const sortItemSchema = z.object({
  id: z.enum(['categoryName', 'categoryDescription', 'status']),
  desc: z.boolean(),
});

const DiscountCategorySearchSchema = z.object({
  sort: z
    .preprocess((val) => {
      if (typeof val === 'string') {
        try {
          return JSON.parse(val);
        } catch {
          return undefined;
        }
      }
      return val;
    }, z.array(sortItemSchema))
    .optional(),
  q: z
    .any()
    .optional()
    .transform((val) => (val == null ? undefined : String(val))),
  page: z.coerce.number().positive().catch(0),
  fetch: z.coerce.number().positive().max(100).catch(10),
});

export const Route = createFileRoute('/(app)/configuration-management/discount-management/')({
  loader: ({ context }) => {
    const {
      dialogFormStore: { onOpen },
    } = context;

    const renderActions = (
      <Can rule={{ allOf: [PERMS.discount.category.assignClub] }}>
        <div>
          <Button
            variant="outline"
            className="border-foreground"
            onClick={() => {
              onOpen({
                modalTitle: 'configurationManagement.discountManagement.assignDiscount.title',
                title: 'Assign New Discount',
                okText: <CrumbTitle id="common.page.modal.submit" />,
                size: 'lg',
                component: <AssignDiscountForm />,
              });
            }}
          >
            <PercentageIcon />
            <CrumbTitle id="configurationManagement.discountManagement.page.buttonTitle.assignDiscount" />
          </Button>
        </div>
      </Can>
    );

    return {
      actions: { renderActions },
    };
  },
  component: RouteComponent,
  validateSearch: (search: SearchSchemaInput) => DiscountCategorySearchSchema.parse(search),
});

function RouteComponent() {
  const { data, isLoading, pagination } = useGetDiscountCategories();

  return (
    <Can rule={{ allOf: [PERMS.discount.category.list] }}>
      <DiscountCategoryTable data={data} pagination={pagination} isLoading={isLoading} />
    </Can>
  );
}
